// File: utils/restApi/use-form.tsx
import * as React from 'react';
import type { IRestModel, RestApiListHooks, SearchParams, UseSearchReturn } from './types';
import { ISelectedRow } from "./selected-row";
import { TypeParser, UrlHandler } from '../form';
import { useAppHookContext } from './useAppHookContext';

type NameValue = {
    name  : string;
    value : string;
};

const getNameValue = (e: React.FormEvent<unknown>): NameValue | null => {
    const input: unknown = (e as { target?: unknown }).target;
    if (!input || typeof input !== 'object') return;

    const t     = input as Record<string, unknown>;
    const name  = typeof t.name === 'string'  ? t.name  : undefined;
    const value = typeof t.value === 'string' ? t.value : undefined;
    if( !name ) return;

    return { name, value };
}

export function useFilteredSearch<TSearch, T extends IRestModel>(
    api           : RestApiListHooks<T>,
    defaultSearch : Partial<TSearch & ISelectedRow> | null = null,
    queryOnLoad   : boolean | null = null
): UseSearchReturn<TSearch, T> {

    if (defaultSearch) {
        defaultSearch.selectedRowId = defaultSearch.selectedRowId ?? null;
    }
    const urlHandler                = React.useMemo(() => new UrlHandler<TSearch>(), []);
    const initialSearch             = urlHandler.updateWithUrlParams(defaultSearch as TSearch & ISelectedRow);
    queryOnLoad                     = queryOnLoad !== false && initialSearch != null;

    const [selectedRow, setSelectedRow]     = React.useState<T | null>(null);
    const [params, setParams] = React.useState<TSearch & ISelectedRow>(() => {
        return initialSearch
            ? (initialSearch as TSearch & ISelectedRow)
            : ({ selectedRowId: null } as TSearch & ISelectedRow);
    });

    const [activeParams, setActiveParams]   = React.useState<TSearch | null>(() =>
        queryOnLoad ? (initialSearch ? ({ ...(initialSearch as TSearch) } as TSearch) : ({} as TSearch)) : null
    );

    /** refresh code to "trick" SWR into refreshing */
    const [refreshTick, setRefreshTick] = React.useState(0);
    /** control to prevent selectedRow from refreshing early and flickering page. */
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    const setField = React.useCallback((name: keyof TSearch & string, value: any) => {
        setParams(prev => ({ ...(prev as any), [name]: value } as TSearch & ISelectedRow));
    }, []);

    const selectRow = React.useCallback((id: string | number, rowData: T) => {
        setParams(prev => ({ ...(prev as any), selectedRowId: id } as TSearch & ISelectedRow));
        
        const base = (activeParams ? { ...(activeParams as any) } : { ...(paramsRef.current as any) });
        urlHandler.pushSearchUrl({ ...base, selectedRowId: id } as any);
        
        setSelectedRow(rowData);
    }, [activeParams, urlHandler]);

    const unselectRow = React.useCallback(() => {
        setParams(prev => ({ ...(prev as any), selectedRowId: null } as TSearch & ISelectedRow));
        
        const base = (activeParams ? { ...(activeParams as any) } : { ...(paramsRef.current as any) });
        urlHandler.pushSearchUrl({ ...base, selectedRowId: null } as any);
        
        setSelectedRow(null);
    }, [activeParams, urlHandler]);

    const handleStringChange = React.useCallback((e: React.FormEvent<unknown>) => {
        const { name, value } = getNameValue(e) || {};
        if( name ) setField(name as keyof TSearch & string, value);
    }, [setField]);

    const handleIntChange = React.useCallback((e: React.FormEvent<unknown>) => {
        const { name, value }   = getNameValue(e) || {};
        const result            = TypeParser.getIntOrDefault(value, null);

        if( name ) setField(name as keyof TSearch & string, result);
    }, [setField]);

    const handleBoolChange = React.useCallback((e: React.FormEvent<unknown>) => {
        const { name, value }   = getNameValue(e) || {};
        const result            = TypeParser.getBoolOrDefault(value, null);
        
        if( name ) setField(name as keyof TSearch & string, result);
    }, [setField]);

    const getStringToggleGroupHandler = React.useCallback(( fieldName : string ) => {
        const handler = (...args: any[]) => {
            const first = args[0]; // could be values array or single value; older typings also pass (values, event)
            const values: any[] = Array.isArray(first) ? first : [first];
            const arr = values.map(v => String(v));
            setField(fieldName as keyof TSearch & string, arr);
        };
        return handler as any;
    }, [setField]);

    // ToggleButtongroup doesn't send the event out...so we have to give the field name
    // to the handler (unless I missed something in the way that works)
    const getIntToggleGroupHandler = React.useCallback(( fieldName : string ) => {
        const handler = (...args: any[]) => {
            const first = args[0];
            const values: any[] = Array.isArray(first) ? first : [first];
            const arr = values.map(v => Number(v));
            setField(fieldName as keyof TSearch & string, arr);
        };
        return handler as any;
    }, [setField]);

    // a getHandler instead of handleChagne because We need to pass in the date format
    // that we want on the way back out to the object...
    const getDateHandler = React.useCallback((format: string) => {
        return (e: React.FormEvent<unknown>) => {
            const { name, value } = getNameValue(e) || {};
            const parsedDate      = TypeParser.getDateByFormatOrDefault(value, format, null);
            const formatted       = parsedDate ? TypeParser.formatDate(parsedDate, format) : undefined;
            if (name) setField(name as keyof TSearch & string, formatted);
        };
    }, [setField]);

    const paramsRef = React.useRef(params);
    React.useEffect(() => { paramsRef.current = params; }, [params]);

    const handleSearch = React.useCallback((e?: React.FormEvent) => {
        if (e && typeof (e as any).preventDefault === 'function') {
            (e as React.FormEvent).preventDefault();
            (e as React.FormEvent).stopPropagation?.();

            // Push full params (selection stays reflected in URL)
            urlHandler.pushSearchUrl(paramsRef.current);
        }

        const { selectedRowId: _omit, ...withoutSelection } = (paramsRef.current as any);
        setActiveParams({ ...(withoutSelection as TSearch) });
    }, [urlHandler]); // No [params] dependency

    /**
     * our code won't re-query the data when there's no search parameter changes, so
     * added a refreshTick that allows us to force a requery when needed (e.g. form.setOnSaveComplete callback)
     */
    const refresh = React.useCallback(() => {
        if (activeParams === null) {
            const { selectedRowId: _omit, ...withoutSelection } = (paramsRef.current as any);
            setActiveParams({ ...(withoutSelection as TSearch) });
            return;
        }
        
        setIsRefreshing(true);
        setRefreshTick(t => t + 1);
    }, [activeParams]);

    const equals = (val1 : unknown, val2 : unknown) : boolean => {
        return String(val1) === String(val2);
    }

    // Execute search only when activeParams is set (or on load if allowed)
    const hasActive = activeParams !== null;
    const result    = api.useSearch(
        hasActive
            ? ({ ...((activeParams as unknown) as SearchParams), __refresh: refreshTick } as SearchParams)
            : null
    );
    
    const hasPayload = Array.isArray((result as any)?.payload);
    const isLoading  = hasActive && !hasPayload;
    const data      = (hasPayload ? ((result as any).payload as T[]) : ([] as T[]));
    const isLoaded  = hasActive ? result !== undefined : true;
    const message   = hasActive ? (result?.message || '') : '';

    /**
     * part of our prevent flicker on refresh 
     */
    React.useEffect(() => {
        if (isRefreshing && hasActive && hasPayload) {
            setIsRefreshing(false);
        }
    }, [isRefreshing, hasActive, hasPayload]);

    /**
     * Previously, refreshing/loading would reset selectedRow causing sidebar, etc., to disappear then re-appear after load
     * So, we need to preserve selection until data is back and only clear it if it's not in the results.
     */
    React.useEffect(() => {
        if (isLoading || isRefreshing) return;

        const id = params.selectedRowId;
        if (id == null) {
            if (selectedRow !== null) setSelectedRow(null);
            return;
        }
        const found = data.find(d => String((d as any).id) === String(id));
        
        if (!found) return;
        if (!selectedRow || String((selectedRow as any).id) !== String(id)) {
            setSelectedRow(found);
        }
    }, [isLoading, isRefreshing, data, params.selectedRowId]);

    React.useEffect(() => {
        if(!queryOnLoad) return;
        handleSearch();
    }, [queryOnLoad, handleSearch]);

    return {
        params,
        data,
        isLoaded,
        message,
        selectedRow,
        equals,
        handleStringChange,
        handleIntChange,
        handleBoolChange,
        getDateHandler,
        getStringToggleGroupHandler,
        getIntToggleGroupHandler,
        setField,
        handleSearch,
        refresh,
        selectRow,
        unselectRow
    };
}
